// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { produce } from 'immer';
import { isDefined } from '@shared/utils';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import type {
  IlliquidAsset,
  IlliquidAssetCurrency,
  IlliquidAssetLocalCurrencyValues,
  IlliquidAssetResponse,
} from '@shared/data-access/types/engine-types';

/**
 * Gets the exchange rate for a currency, defaulting to 1 if not found.
 *
 * @param currency - The currency code.
 * @param exchangeRates - A record of exchange rates.
 * @returns The exchange rate for the currency or 1 if not found.
 */
const getExchangeRate = (
  currency: string | null | undefined,
  exchangeRates: Record<string, number>,
): number => {
  return currency ? exchangeRates[currency] ?? 1 : 1;
};

/**
 * Converts a numeric value using the provided exchange rate if the value is not
 * null.
 *
 * @param value - The numeric value to convert (can be null or undefined).
 * @param rate - The exchange rate to apply.
 * @returns The converted value or null if the original value was null/undefined.
 */
const convertValue = (
  value: number | null | undefined,
  rate: number,
): number | null => {
  return isDefined(value) ? value * rate : null;
};

/**
 * Mutates a draft asset: snapshots `local_currency_values`, sets display currency,
 * and converts monetary fields. Event rows are left in their native currency.
 */
const convertIlliquidAssetInPlace = (
  draft: IlliquidAssetResponse,
  exchangeRates: Record<string, number>,
  accountCurrency: AccountCurrency,
): void => {
  const row = draft as IlliquidAssetResponse & {
    currency: IlliquidAssetCurrency | AccountCurrency;
    local_currency_values?: IlliquidAssetLocalCurrencyValues;
  };

  const rate = getExchangeRate(String(row.currency), exchangeRates);

  row.local_currency_values = {
    currency: row.currency as IlliquidAssetCurrency,
    current_valuation: row.current_valuation,
    acquisition_value: row.acquisition_value,
    total_commitment: row.total_commitment,
  };

  row.currency = accountCurrency as unknown as IlliquidAssetCurrency;
  row.current_valuation = convertValue(row.current_valuation, rate);
  row.acquisition_value = convertValue(row.acquisition_value, rate);
  row.total_commitment = convertValue(row.total_commitment, rate);
};

/**
 * Converts illiquid asset monetary fields to the account currency and preserves
 * native amounts under `local_currency_values`, using immer for immutable updates.
 *
 * @param asset - Engine response row (native currency and amounts).
 * @param exchangeRates - A record of exchange rates where the key is the currency
 *   code and the value is the rate into the account currency.
 * @param accountCurrency - Target display currency.
 * @returns A new {@link IlliquidAsset} with converted top-level amounts.
 */
export const convertIlliquidAssetCurrency = (
  asset: IlliquidAssetResponse,
  exchangeRates: Record<string, number>,
  accountCurrency: AccountCurrency,
): IlliquidAsset => {
  return produce(asset, draft => {
    convertIlliquidAssetInPlace(draft, exchangeRates, accountCurrency);
  }) as unknown as IlliquidAsset;
};

/**
 * Converts each illiquid asset to the account currency.
 *
 * @param assets - Engine response rows.
 * @param exchangeRates - Currency code to rate into the account currency.
 * @param accountCurrency - Target display currency.
 * @returns New array of {@link IlliquidAsset} rows.
 */
export const convertIlliquidAssetsCurrency = (
  assets: IlliquidAssetResponse[],
  exchangeRates: Record<string, number>,
  accountCurrency: AccountCurrency,
): IlliquidAsset[] => {
  if (assets.length === 0) {
    return [];
  }

  return assets.map(asset =>
    convertIlliquidAssetCurrency(asset, exchangeRates, accountCurrency),
  );
};
